import {
  Avatar,
  Distribute,
  Icon,
  SvgComment,
  SvgTagFaces,
  Text,
} from '@loomhq/lens';
import classnames from 'classnames';
import moment from 'moment';
import React from 'react';
import { useDispatch } from 'react-redux';

import { requestVideoPlayerSeekToTs } from 'actions/videoPlayer';
import { CLOUDFRONT_URI } from 'constants/routes';
import { secondsToVideoTS } from 'utilities/time';

import { AnonAvatar } from './avatars';
import { toPercentOfDuration } from './utils';

import { emojiTypeToPath } from 'components/ui-components/emoji';

import styles from './styles.module.less';

export const Reaction = ({ type, ts, content, onClick, duration }) => {
  const ref = React.useRef();
  const className = type + ' opaque';
  const isCommentType = type === 'comment';
  const style = { left: toPercentOfDuration(ts, duration) };

  React.useLayoutEffect(() => {
    const el = ref?.current;
    const parentRect = el.parentElement?.getBoundingClientRect();
    const childRect = el.getBoundingClientRect();
    const pushedOut = childRect.right - parentRect.right;

    if (pushedOut > 0) {
      // if emoji is partially or fully out of the container box
      // push it back in
      el.style.transform = `translateX(${-1 * pushedOut}px)`;
    }
  });

  let title = '';
  const path = emojiTypeToPath[content];

  if (isCommentType) {
    title = content;
  }

  return (
    <div
      className={className}
      style={style}
      title={title}
      onClick={onClick}
      ref={ref}
    >
      {!path ? null : <img src={path} />}
    </div>
  );
};

export const Segment = ({ type, width, children = null, ...props }) => {
  const style = { width: width + '%' };

  if (type === 'played') {
    style.backgroundColor = '#70E5A9';
  }

  if (type === 'replayed') {
    style.backgroundColor = '#00C1FF';
  }

  if (type === 'filler') {
    style.backgroundColor = 'transparent';
  }

  return (
    <div style={style} {...props} className={type}>
      {children && children}
    </div>
  );
};

export const ViewerInfo = ({ user = {}, events = [] }) => {
  const comments = events.filter(evt =>
    ['comment', 'reply'].includes(evt.type)
  );
  const reactions = events.filter(evt => evt.type === 'reaction');

  return (
    <article className={classnames(styles.flex, styles.viewerInfo)}>
      <ViewerAvatar
        avatar={user.avatar}
        name={user.name}
        color={user.color}
        variant={user.variant}
      />
      <div className={styles.fit}>
        <ViewerName {...user} />
        <footer className={classnames(styles.flex, styles.footer)}>
          <ViewerEventCounter type="comment" items={comments || []} />
          <ViewerEventCounter type="reaction" items={reactions || []} />
        </footer>
      </div>
    </article>
  );
};

const ViewerEventCounter = ({ type, items = [] }) => {
  const tot = items.length;
  const Ico = type === 'comment' ? SvgComment : SvgTagFaces;

  return (
    <Distribute gap="small" alignment="center">
      <Icon icon={<Ico />} size={2} />
      <Text>
        {tot} {type}
        {tot !== 1 ? 's' : ''}
      </Text>
    </Distribute>
  );
};

export const ViewerAvatar = ({
  avatar,
  size = 'large',
  color,
  variant = 1,
}) => {
  const props = {
    size,
    ...(avatar && { imageSrc: `${CLOUDFRONT_URI}/${avatar}` }),
  };

  return (
    <Avatar {...props}>
      {!avatar && <AnonAvatar color={color} variant={variant} />}
    </Avatar>
  );
};

export const ViewerName = props => (
  <div>
    <Text size="large" htmlTag="h3">
      {props.name}
    </Text>
  </div>
);
export const ViewsList = props => {
  return (
    <div className="viewsList">
      {props.views.map((ts, i) => {
        const timestamp = Number(ts);
        const date = moment(timestamp).format('MMMM Do, YYYY h:mma');

        return (
          <Text variant="body" key={i}>
            <time>{date}</time>
          </Text>
        );
      })}
    </div>
  );
};

export const EventsList = ({ events = [] }) => {
  const dispatch = useDispatch();
  const actions = {
    comment: 'Commented at',
    reaction: 'Reacted at',
    reply: 'Replied at',
  };

  if (!events.length) {
    return null;
  }

  const scrollTo = evt => () => {
    const el = document.querySelector(`#${evt.type}-${evt.id}`);

    if (!el) {
      return;
    }

    el.classList.remove('highlightedComment');
    window.requestAnimationFrame(() => {
      el.classList.add('highlightedComment');
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const playVideoAt = ts => () => {
    dispatch(requestVideoPlayerSeekToTs(ts));
  };

  return (
    <>
      <Text fontWeight="semiBold">Activity</Text>
      <div className={styles.eventsList}>
        {events.sort(sortCompareByTimestamp).map((evt, i) => {
          const action = actions[evt.type];
          const path = emojiTypeToPath[evt.content];
          const isComment = ['comment', 'reply'].includes(evt.type);

          return (
            <Distribute
              alignment="center"
              gap={1}
              key={i}
              className={classnames(styles.eventItem, styles.isClickable)}
              onClick={isComment ? scrollTo(evt) : playVideoAt(evt.ts)}
            >
              {action}
              <time>{secondsToVideoTS(evt.ts)}</time>
              {path && (
                <img src={path} alt={`${action} ${secondsToVideoTS(evt.ts)}`} />
              )}
            </Distribute>
          );
        })}
      </div>
    </>
  );
};

function sortCompareByTimestamp({ ts }, { ts: ts2 }) {
  if (ts > ts2) {
    return 1;
  }

  if (ts2 > ts) {
    return -1;
  }

  return 0;
}
